// ============================================================================
// SUPPRIMER UN ARTICLE — la fenêtre de la fiche (maquette : `artDelCheck`, valeurs `ad*`), pour une fiche entière ou une
// seule déclinaison (la reprise d'une déclinaison rattachée par erreur à un autre article).
//
// ⚠️ L'ÉCRAN NE JUGE RIEN : il affiche ce que le SERVEUR a jugé (`GET /articles/deletion`), et la suppression rejuge dans
//    sa propre transaction. Un article vendu entre l'ouverture et le clic est refusé, la fenêtre relit et le dit.
// ⚠️ LES RAISONS SONT DES CODES : le serveur nomme la table qui cite l'article, cet écran la dit dans la langue du compte.
// ⚠️ SUPPRESSION DÉFINITIVE : aucun geste ne la défait, la fenêtre le dit avant le clic — et la requête porte le magasin
//    que l'onglet affiche, que le serveur compare à celui de la session.
// ============================================================================
import { useEffect, useState } from 'react';
import { date, dateTime } from '@orgazone/date-format';
import { codeCaisse, Icon, Modal } from '@orgazone/ui-web';
import { api, type ArticleDeleteBlock, type ArticleDeleteTarget, type ArticleDeletionCheck } from '../api';
import { key, keyText, t, type DeclaredKey } from '../i18n';
import { useDisplayedStoreGln } from '../store-context';

/** Ce que dit chaque table qui cite un article — une phrase par sorte de document, jamais le nom technique. */
const CITED_BY: Record<string, DeclaredKey> = {
  fiscal_piece_line: key('Il a été vendu en caisse.'),
  abandoned_sale_line: key('Il a été scanné dans une vente abandonnée en caisse.'),
  stock: key('Il a eu du stock.'),
  stock_movement: key('Il a eu du stock.'),
  purchase_entry: key('Il a été entré en stock par un bon de livraison.'),
  delivery_note_line: key('Il figure sur un bon de livraison.'),
  supplier_order_line: key('Il figure sur une commande fournisseur.'),
  retrocession_line: key('Il figure sur une rétrocession.'),
  supplier_return_line: key('Il figure sur un retour fournisseur.'),
  inventory_count: key('Il a été compté dans un inventaire.'),
  inventory_report_line: key('Il a été compté dans un inventaire.'),
  inventory_snapshot: key('Il a été compté dans un inventaire.'),
  stock_zone_line: key('Il a été compté par Mobile Partner.'),
  promotion_target: key('Une promotion le cible : retirez-le de la promotion d’abord.'),
  quick_tile: key('Il est publié dans les articles rapides : retirez-le du studio et publiez d’abord.'),
  article_draft: key('Un brouillon d’article créé en caisse le désigne.'),
};

/** La phrase d'un empêchement. */
export function blockText(b: ArticleDeleteBlock): string {
  switch (b.code) {
    case 'referenced': return CITED_BY[b.table] ? t(CITED_BY[b.table]!) : t('Il est cité ailleurs (« {table} »).', { table: b.table });
    case 'sales_exclusion': return t('Une campagne de soldes l’exclut : retirez-le de ses exclusions d’abord.');
    case 'held_sale': return t('Une vente mise en attente en caisse le contient : reprenez-la ou supprimez-la d’abord.');
    case 'quick_draft': return t('Il est dans le brouillon du studio des articles rapides : retirez-le d’abord.');
    case 'no_gencode': return t('Une déclinaison n’a pas de code-barres : aucune écriture ne peut l’atteindre.');
    case 'offline': return b.kind === 'register'
      ? t('La caisse {caisse} n’est pas en ligne (dernier contact {quand}) : elle pourrait le vendre en ce moment. Reconnectez-la, ou attendez le {date}.', { caisse: codeCaisse(b.device), quand: dateTime(b.lastSeenAt), date: date(b.unblocksAt) })
      : t('Le terminal {terminal} n’est pas en ligne (dernier contact {quand}) : il pourrait le compter en ce moment. Reconnectez-le, ou attendez le {date}.', { terminal: b.device, quand: dateTime(b.lastSeenAt), date: date(b.unblocksAt) });
  }
}

/** Les raisons à dire, une fois chacune — deux tables de stock disent la même chose, l'écran ne la répète pas. */
export function blockTexts(blocks: readonly ArticleDeleteBlock[]): string[] {
  return [...new Set(blocks.map(blockText))];
}

/** L'empêchement vient-il d'un usage (stock, ventes) — auquel cas l'archivage est la réponse, et la fenêtre le dit ? */
const USED = new Set(['fiscal_piece_line', 'stock', 'stock_movement', 'purchase_entry']);

/** Ce que la fenêtre vérifie, dit au commerçant quand tout est permis (dessin : « Vérifié avant suppression »). */
const CHECKED: readonly DeclaredKey[] = [
  key('Jamais entré en stock'), key('Aucune vente ni mouvement de stock'),
  key('Dans aucune commande, aucun BL ni aucun inventaire'), key('Dans aucun article rapide'),
  key('Aucune caisse ni aucun terminal hors ligne'),
];

/** Le motif d'un refus de la suppression, dans la langue du compte — jamais la clé technique du serveur. */
function refusalText(e: unknown): string {
  const code = (e as { key?: unknown }).key;
  if (code === 'deletion_refused') return t('Depuis l’ouverture de la fenêtre, quelque chose empêche la suppression : voici ses raisons.');
  if (code === 'store_changed') return t('Le magasin actif a changé dans un autre onglet : rechargez la page avant de supprimer.');
  if (code === 'forbidden') return t('Votre compte n’a pas le droit de supprimer des articles.');
  return e instanceof Error ? e.message : String(e);
}

export function ArticleDeleteDialog({ open, target, name, code, onClose, onDeleted }: {
  open: boolean;
  target: ArticleDeleteTarget;
  name: string;
  code: string | null;
  onClose: () => void;
  onDeleted: (r: { deleted: number; modelGone: boolean }) => void;
}) {
  const storeGln = useDisplayedStoreGln();
  const [check, setCheck] = useState<ArticleDeletionCheck | null>(null);
  const [readFailure, setReadFailure] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // ⚠️ UNE CLÉ PAR OUVERTURE : un double clic ne rejoue pas la suppression.
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const targetKey = 'model' in target ? `m:${target.model}` : `g:${target.gencode}`;

  useEffect(() => {
    if (!open) return;
    setFailure(null);
    setAttempt(0);
    setIdempotencyKey(`del:${targetKey}:${crypto.randomUUID()}`);
  }, [open, targetKey]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setCheck(null); setReadFailure(null);
    api.articleDeletion(target)
      .then((r) => { if (alive) setCheck(r); })
      .catch((e: unknown) => { if (alive) setReadFailure(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- la cible se lit par sa clé
  }, [open, targetKey, attempt]);

  const blocks = check?.blocks ?? null;
  // ⚠️ UNE CIBLE DÉJÀ SUPPRIMÉE (ailleurs, ou avant une coupure) n'est pas « vérifiée » : il n'y a plus rien à supprimer.
  const gone = check !== null && check.variants === 0;
  const can = check !== null && !gone && check.blocks.length === 0 && storeGln !== null;
  // ⚠️ LA DERNIÈRE DÉCLINAISON EMPORTE LA FICHE : le serveur le dit, la fenêtre l'annonce comme une fiche entière.
  const whole = check?.wholeModel ?? 'model' in target;
  const go = async () => {
    if (!can || writing || !storeGln) return;
    setWriting(true);
    setFailure(null);
    try {
      const r = await api.deleteArticle(target, idempotencyKey, storeGln);
      onDeleted({ deleted: r.deleted, modelGone: r.modelGone });
    } catch (e) {
      // ⚠️ REFUSÉE PAR LE SERVEUR (vendu, ou une caisse partie entre-temps) : la fenêtre relit ses raisons et les montre.
      setFailure(refusalText(e));
      setAttempt((n) => n + 1);
    } finally {
      setWriting(false);
    }
  };

  const used = (blocks ?? []).some((b) => b.code === 'referenced' && USED.has(b.table));
  const refused = blocks !== null && blocks.length > 0;
  const title = gone ? t('Cet article n’existe plus')
    : refused ? (whole ? t('Cet article ne peut pas être supprimé') : t('Cette déclinaison ne peut pas être supprimée'))
      : (whole ? t('Supprimer cet article ?') : t('Supprimer cette déclinaison ?'));
  const mono = { fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '.08em', textTransform: 'uppercase' as const, color: 'var(--ink-500)', fontWeight: 600 };

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="sm"
      icon={refused || gone ? 'lock' : 'trash'}
      tone={can ? 'rose' : 'ink'}
      title={title}
      subtitle={can ? t('Suppression définitive — elle ne peut pas être annulée.') : undefined}
      // ⚠️ Une fenêtre ne se ferme pas PENDANT l'écriture : on ne saurait pas dire si l'article existe encore.
      blockClose={() => writing}
      footer={(
        <div style={{ display: 'grid', gridTemplateColumns: can ? '1fr 1fr' : '1fr', gap: 10, width: '100%' }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={writing}>{can ? t('Annuler') : t('Fermer')}</button>
          {can && (
            <button type="button" className="btn-cta" aria-disabled={writing} onClick={() => { if (!writing) void go(); }}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'var(--rose-fg)', color: 'var(--paper)' }}>
              <Icon name="trash" size={15} />
              {writing ? t('Suppression…') : whole ? t('Supprimer l’article') : t('Supprimer la déclinaison')}
            </button>
          )}
        </div>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)' }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
            {code && <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: '11.5px', color: 'var(--ink-500)' }}>{code}</span>}
          </span>
          {check !== null && !gone && (
            <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-700)', fontSize: '11.5px', fontWeight: 600, flexShrink: 0 }}>
              {t('{n} déclinaison', { n: check.variants })}</span>
          )}
        </div>

        {failure && (
          <p role="alert" style={{ margin: 0, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', border: '1px solid var(--rose-fg)', fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.5 }}>
            {failure}</p>
        )}
        {readFailure && <p role="alert" style={{ margin: 0, fontSize: '12.5px', color: 'var(--danger-ink)' }}>{t('Vérification impossible : {motif}', { motif: readFailure })}</p>}
        {check === null && !readFailure && <p role="status" style={{ margin: 0, fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Vérification…')}</p>}
        {gone && (
          <p role="status" style={{ margin: 0, fontSize: '12.5px', color: 'var(--ink-700)', lineHeight: 1.5 }}>
            {t('Il a déjà été supprimé : il n’y a plus rien à supprimer.')}</p>
        )}
        {check !== null && !gone && !refused && storeGln === null && (
          <p role="alert" style={{ margin: 0, fontSize: '12.5px', color: 'var(--danger-ink)' }}>
            {t('Aucun magasin actif sur cet onglet : rechargez la page avant de supprimer.')}</p>
        )}

        {can && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={mono}>{t('Vérifié avant suppression')}</span>
              {CHECKED.map((l) => (
                <span key={keyText(l)} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: '12.5px', color: 'var(--ink-800)' }}>
                  <span aria-hidden style={{ width: 18, height: 18, borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="check" size={11} stroke={3} /></span>
                  {t(l)}
                </span>
              ))}
            </div>
            <p role="note" style={{ display: 'flex', gap: 9, alignItems: 'flex-start', margin: 0, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: '12px', fontWeight: 600, lineHeight: 1.45 }}>
              <Icon name="alert" size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{whole
                ? t('La fiche, ses {declinaisons} et leurs codes-barres disparaissent définitivement — au siège et sur chaque caisse.', { declinaisons: t('{n} déclinaison', { n: check.variants }) })
                : t('Cette déclinaison et son code-barres disparaissent définitivement — au siège et sur chaque caisse.')}</span>
            </p>
            {whole && !('model' in target) && (
              <p role="note" style={{ margin: 0, fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.5 }}>
                {t('C’est la dernière déclinaison de l’article : la fiche entière disparaît avec elle, ses photos et ses champs personnalisés compris.')}</p>
            )}
          </>
        )}

        {refused && (
          <>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {blockTexts(blocks).map((text) => (
                <li key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.45 }}>
                  <Icon name="lock" size={13} style={{ flexShrink: 0, marginTop: 2, color: 'var(--ink-400)' }} />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            {used && (
              <p style={{ margin: 0, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.5 }}>
                {t('Un article qui a déjà eu du stock garde sa fiche pour l’historique. Quand il n’est plus en stock nulle part, archivez-le (Réglages → Archivage des articles) pour le sortir des recherches.')}</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
